import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';
import Shift from 'resources/Shift';

import { getCurrentUserId } from 'selectors/session';

import {
  selectClosingTime,
  selectIsShiftsAtThisLocationOnly,
  selectJobIdsForCurrentLocation,
  selectOpenTime,
} from 'features/scheduleBuilder/ScheduleBuilderView/selectors';
import { selectClaimRequestsByShiftId } from 'features/scheduleBuilder/slices/claimRequests/slice';
import { selectShiftConflictType } from 'features/scheduleBuilder/slices/conflicts/slice';
import { selectShiftNotesQuotaAttribute } from 'features/scheduleBuilder/slices/shiftNotesQuotas/slice';
import {
  selectJobIdsForUser,
  selectUserJobs,
} from 'features/scheduleBuilder/slices/users/slice';

import { clockFromTime, dateFromTime } from 'util/dateTime';

import shiftSortCompare from '../utils/shiftSortCompare';

import { selectConflictsEnabled } from './index';

export const selectShiftTimeRange = createCachedSelector(
  (state, props) => Shift.selectAttribute(state, 'startAt', props.shiftId),
  (state, props) => Shift.selectAttribute(state, 'endAt', props.shiftId),
  selectOpenTime,
  selectClosingTime,
  (state, props) => props.startAt,
  (state, props) => props.endAt,
  (startAt, endAt, openTime, closingTime, initialStartAt, initialEndAt) => {
    let from = startAt ? clockFromTime(startAt) : initialStartAt;
    if (from === undefined) {
      from = `${openTime}:00`;
    }

    let to = endAt ? clockFromTime(endAt) : initialEndAt;
    if (to === undefined) {
      to = `${closingTime}:00`;
    }

    return {
      from,
      to,
    };
  }
)((_, props) => `selectShiftTimeRange-${props.shiftId}`);

export const selectIsShiftNoteAllowed = createCachedSelector(
  (state, props) =>
    selectShiftNotesQuotaAttribute({
      state,
      attribute: 'unlimited',
      id: props.shiftNoteQuotaId,
    }),
  (state, props) =>
    selectShiftNotesQuotaAttribute({
      state,
      attribute: 'used',
      id: props.shiftNoteQuotaId,
    }),
  (state, props) =>
    selectShiftNotesQuotaAttribute({
      state,
      attribute: 'allowed',
      id: props.shiftNoteQuotaId,
    }),
  (hasNotesAccess, numNotesUsed, numNotesAllowed) =>
    hasNotesAccess || numNotesUsed < numNotesAllowed
)((_, props) => `selectIsShiftNoteAllowed-${props.shiftNoteQuotaId}`);

export const selectNumLimitedNotesLeftInQuota = createCachedSelector(
  (state, props) =>
    selectShiftNotesQuotaAttribute({
      state,
      attribute: 'unlimited',
      id: props.shiftNoteQuotaId,
    }),
  (state, props) =>
    selectShiftNotesQuotaAttribute({
      state,
      attribute: 'used',
      id: props.shiftNoteQuotaId,
    }),
  (state, props) =>
    selectShiftNotesQuotaAttribute({
      state,
      attribute: 'allowed',
      id: props.shiftNoteQuotaId,
    }),
  (hasNotesAccess, numNotesUsed, numNotesAllowed) =>
    hasNotesAccess || numNotesUsed > numNotesAllowed
      ? 0
      : numNotesAllowed - numNotesUsed
)((_, props) => `selectNumLimitedNotesLeftInQuota-${props.shiftNoteQuotaId}`);

export const selectHideWithNoConflicts = createCachedSelector(
  selectShiftConflictType,
  selectConflictsEnabled,
  (conflictType, enabled) => enabled && !conflictType
)((_, props) => `selectHighlightConflict-${props.id}`);

export const selectAllShiftsOnDate = createCachedSelector(
  Shift.selectAllEntities,
  selectIsShiftsAtThisLocationOnly,
  selectJobIdsForUser,
  selectJobIdsForCurrentLocation,
  (_, { date }) => date,
  (shifts, isShiftsAtThisLocationOnly, jobIds, jobIdsCurrentLocation, date) => {
    let filteredShifts = shifts.filter(
      s =>
        dateFromTime(s.attributes.startAt) === date &&
        s.relationships?.owner?.data?.type === 'job' &&
        jobIds.includes(s.relationships?.owner?.data?.id)
    );

    if (isShiftsAtThisLocationOnly) {
      filteredShifts = filteredShifts.filter(s =>
        jobIdsCurrentLocation.includes(s.relationships?.owner?.data?.id)
      );
    }

    return filteredShifts.sort(shiftSortCompare);
  }
)(
  (_, { jobId, userId, date }) =>
    `selectAllShiftsOnDate-${jobId}-${userId}-${date}`
);

export const selectAllLocationIdsForUser = createSelector(
  state => {
    const userId = getCurrentUserId(state);
    const jobs = selectUserJobs(state, userId);
    return jobs
      .map(job => job.location?.id)
      .filter(Boolean)
      .join(',');
  },
  ids => (ids.length ? ids.split(',') : [])
);

export const selectShiftIdsForJobOnDate = createCachedSelector(
  (state, props) =>
    selectAllShiftsOnDate(state, props)
      .map(({ id }) => id)
      .join(','),
  ids => (ids.length ? ids.split(',') : [])
)((_, { userId, date }) => `selectShiftIdsForJobOnDate-${userId}-${date}`);

export const selectShiftHasClaimRequests = createSelector(
  (state, { id }) => selectClaimRequestsByShiftId(state, id),
  claimRequests => claimRequests?.length > 0
);
